/**
 * Keeps the page a user asked for across a login round trip.
 *
 * Every redirect to `/login` carries the current in-app path as `returnTo`, and the
 * login and SSO callback pages send the user back there once authenticated. Only
 * same-origin relative paths are honoured, so the parameter cannot become an open redirect.
 */

export const LOGIN_PATH = '/login';
export const SSO_CALLBACK_PATH = '/sso-callback';
export const RETURN_TO_PARAM = 'returnTo';

const HOME = '/';

type LocationLike = { pathname: string; search: string; hash: string };

const isAuthPage = (pathname: string) => pathname === LOGIN_PATH || pathname === SSO_CALLBACK_PATH;

/** A path is safe to return to when it stays inside this app: absolute, without scheme or host. */
export function isSafeReturnTo(path: string): boolean {
	if (!path.startsWith('/')) return false;
	// "//host" and "/\host" are treated as protocol-relative URLs by browsers.
	if (path.startsWith('//') || path.startsWith('/\\')) return false;
	const pathname = path.split(/[?#]/, 1)[0];
	return !isAuthPage(pathname);
}

/** `path` with `returnTo` attached, unless the target is just the home page. */
export function withReturnTo(path: string, returnTo: string): string {
	if (returnTo === HOME) return path;
	return `${path}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`;
}

/** The login page URL that brings the user back to `location` after authentication. */
export function loginPathFor(location: LocationLike): string {
	if (isAuthPage(location.pathname)) return LOGIN_PATH;
	return withReturnTo(LOGIN_PATH, `${location.pathname}${location.search}${location.hash}`);
}

/** Where a finished login should go: the requested in-app path, or home when absent or unsafe. */
export function resolveReturnTo(search: string): string {
	const requested = new URLSearchParams(search).get(RETURN_TO_PARAM);
	return requested && isSafeReturnTo(requested) ? requested : HOME;
}
